import { useQuery } from '@tanstack/react-query';
import { divisionService, supplierService } from '@services';
import type { DrillDownFilters } from '@app-types';
import type { DrillDownNames } from '@utils/assessmentDrillDownFilters';

/** Option sets change rarely, so one fetch per session is enough. */
const STALE_TIME = 5 * 60 * 1000;

// The query key is shared with pages that once cached something else under it, so the shape is checked.
const namesById = (items: ReadonlyArray<{ id: number; name: string }> | undefined) =>
  new Map((Array.isArray(items) ? items : []).map((item) => [String(item.id), item.name]));

/**
 * The names behind the ids of the drill-down filters, from the Division and Tier lists other pages already load (same
 * query keys, so the cache is shared). They are fetched only when a filter holds such ids. The Active Divisions list
 * leaves out a deactivated Division, and nothing lists Assessment Domains by id, so those ids stay ids.
 */
export function useDrillDownNames(filters: DrillDownFilters): DrillDownNames {
  const { data: divisions } = useQuery({
    queryKey: ['divisions', 'active'],
    queryFn: () => divisionService.getActive(),
    staleTime: STALE_TIME,
    enabled: Boolean(filters.division_id)
  });
  const { data: tiers } = useQuery({
    queryKey: ['suppliers', 'tiers'],
    queryFn: () => supplierService.getSupplierTiers(),
    staleTime: STALE_TIME,
    enabled: Boolean(filters.tier_id)
  });

  return { division_id: namesById(divisions), tier_id: namesById(tiers) };
}
